import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Route, CheckCircle2, Circle, Clock, Calendar, Target, ChevronDown, ChevronUp, AlertTriangle, MessageSquare, Paperclip, Send, FileText, Download, Loader2, X, Bot } from "lucide-react";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import { Textarea } from "../components/ui/textarea";
import { AgentChat } from "../components/AgentChat";

export default function AccompagnementPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [accompaniments, setAccompaniments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showAgent, setShowAgent] = useState(false);

  // Notes & files state
  const [notes, setNotes] = useState({});
  const [files, setFiles] = useState({});
  const [showNoteForm, setShowNoteForm] = useState(null); // "accId_phaseIdx" or "accId" for general
  const [noteContent, setNoteContent] = useState("");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activePhaseTab, setActivePhaseTab] = useState({}); // { accId: phaseIdx }
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null); // { accId, phaseIndex, taskId }

  const loadData = () => {
    api.get("/accompaniments").then((r) => {
      setAccompaniments(r.data);
      if (r.data.length > 0 && !expandedId) setExpandedId(r.data[0].accompaniment_id);
      setLoading(false);
      // Load notes and files for each accompaniment
      r.data.forEach(acc => {
        loadNotesAndFiles(acc.accompaniment_id);
      });
    }).catch(() => setLoading(false));
  };

  const loadNotesAndFiles = async (accId) => {
    try {
      const [notesRes, filesRes] = await Promise.all([
        api.get(`/accompaniments/${accId}/notes`),
        api.get(`/accompaniments/${accId}/files`),
      ]);
      setNotes(prev => ({ ...prev, [accId]: notesRes.data || [] }));
      setFiles(prev => ({ ...prev, [accId]: filesRes.data || [] }));
    } catch (e) { console.error(e); }
  };

  const submitNote = async (accId) => {
    if (!noteContent.trim()) return;
    setSubmittingNote(true);
    try {
      await api.post(`/accompaniments/${accId}/notes`, { content: noteContent });
      setNoteContent("");
      setShowNoteForm(null);
      loadNotesAndFiles(accId);
    } catch (e) { console.error(e); }
    setSubmittingNote(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    setUploadingFile(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/accompaniments/${uploadTarget.accId}/files?phase_index=${uploadTarget.phaseIndex}&task_id=${uploadTarget.taskId || ""}`, fd);
      loadNotesAndFiles(uploadTarget.accId);
    } catch (err) { console.error(err); }
    setUploadingFile(false);
    setUploadTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerFileUpload = (accId, phaseIndex, taskId = "") => {
    setUploadTarget({ accId, phaseIndex, taskId });
    fileInputRef.current?.click();
  };

  useEffect(() => { loadData(); }, []);

  const toggleTask = async (accId, taskId, currentStatus) => {
    const newStatus = currentStatus === "done" ? "todo" : "done";
    await api.put(`/accompaniments/${accId}/tasks/${taskId}`, { status: newStatus });
    loadData();
  };

  const getProgress = (phases) => {
    if (!phases?.length) return 0;
    let total = 0, done = 0;
    phases.forEach((p) => p.tasks?.forEach((t) => { total++; if (t.status === "done") done++; }));
    return total === 0 ? 0 : Math.round((done / total) * 100);
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  };

  const formulaColors = { START: "#f7c935", PLUS: "#5a2d9c", SMART: "#10b981", BLITZ: "#ef4444" };

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="accompagnement-page">
      <PageHeader
        icon={<Route />}
        title="Mon Accompagnement"
        description="Votre parcours de suivi et les étapes à accomplir"
        testId="accompagnement-header"
        actions={(
          <button onClick={() => setShowAgent(!showAgent)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${showAgent ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] border border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10"}`}
            data-testid="floating-chat-btn">
            <Bot className="w-4 h-4" /> Expert Stratégie
          </button>
        )}
      />

      {accompaniments.length === 0 ? (
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-16 text-center">
          <Route className="w-14 h-14 mx-auto mb-4 text-slate-600" />
          <h3 className="text-xl font-bold mb-2">Aucun accompagnement actif</h3>
          <p className="text-slate-500">Votre parcours d'accompagnement apparaîtra ici une fois mis en place par l'équipe Blitzz.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {accompaniments.map((acc) => {
            const progress = getProgress(acc.phases);
            const color = formulaColors[acc.formula] || "#f7c935";
            const isExpanded = expandedId === acc.accompaniment_id;

            return (
              <div key={acc.accompaniment_id} className="animate-fade-in-up" data-testid={`acc-${acc.accompaniment_id}`}>
                {/* Header Card */}
                <div className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : acc.accompaniment_id)}
                    className="w-full p-6 text-left"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs px-2.5 py-1 rounded font-bold" style={{ background: `${color}20`, color }}>{acc.formula}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${acc.status === "active" ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400"}`}>
                            {acc.status === "active" ? "En cours" : acc.status === "completed" ? "Terminé" : acc.status}
                          </span>
                        </div>
                        <h2 className="text-xl font-bold">{acc.title}</h2>
                        {acc.description && <p className="text-sm text-slate-400 mt-1">{acc.description}</p>}
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-black" style={{ color }}>{progress}%</div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-500 mt-1 ml-auto" /> : <ChevronDown className="w-5 h-5 text-slate-500 mt-1 ml-auto" />}
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="flex items-center gap-6 text-sm text-slate-500 mb-4">
                      {acc.start_date && (
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>Début : {formatDate(acc.start_date)}</span>
                        </div>
                      )}
                      {acc.end_date && (
                        <div className="flex items-center gap-1.5">
                          <Target className="w-4 h-4" />
                          <span>Fin prévue : {formatDate(acc.end_date)}</span>
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <Progress value={progress} className="h-2.5 bg-[#0b0811]" />
                  </button>

                  {/* Timeline Content */}
                  {isExpanded && acc.phases && (
                    <div className="px-6 pb-8 pt-2">
                      {/* Timeline */}
                      <div className="relative ml-4 mt-6">
                        {/* Vertical line */}
                        <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: `${color}30` }} />

                        {acc.phases.map((phase, pi) => {
                          const phaseDone = phase.tasks?.every((t) => t.status === "done");
                          const phaseProgress = phase.tasks?.length > 0
                            ? Math.round((phase.tasks.filter((t) => t.status === "done").length / phase.tasks.length) * 100) : 0;
                          const phaseOverdue = phase.deadline && isOverdue(phase.deadline) && !phaseDone;
                          const phaseFiles = (files[acc.accompaniment_id] || []).filter(f => f.phase_index === pi);

                          return (
                            <div key={pi} className="relative pl-8 pb-10 last:pb-0">
                              {/* Timeline Node */}
                              <div className={`absolute left-0 -translate-x-1/2 w-4 h-4 rounded-full border-2 ${
                                phaseDone ? "border-green-400 bg-green-400" : phaseOverdue ? "border-red-400 bg-red-400/20" : `bg-[#0b0811]`
                              }`} style={{ borderColor: phaseDone ? undefined : phaseOverdue ? undefined : color }} />

                              {/* Phase Header */}
                              <div className="mb-4">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <h3 className="text-base font-bold">{phase.name}</h3>
                                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `${color}15`, color }}>
                                    {phaseProgress}%
                                  </span>
                                  {phase.period && (
                                    <span className="text-xs text-slate-500 flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {phase.period}
                                    </span>
                                  )}
                                  {phase.deadline && (
                                    <span className={`text-xs flex items-center gap-1 ${phaseOverdue ? "text-red-400" : "text-slate-500"}`}>
                                      <Calendar className="w-3 h-3" /> {formatDate(phase.deadline)}
                                      {phaseOverdue && <AlertTriangle className="w-3 h-3" />}
                                    </span>
                                  )}
                                </div>
                                {phase.description && <p className="text-sm text-slate-500 mt-1">{phase.description}</p>}
                              </div>

                              {/* Tasks */}
                              <div className="space-y-2">
                                {phase.tasks?.map((task) => {
                                  const taskOverdue = task.deadline && isOverdue(task.deadline) && task.status !== "done";
                                  return (
                                    <div key={task.id} className="flex items-center gap-2">
                                      <button
                                        onClick={() => toggleTask(acc.accompaniment_id, task.id, task.status)}
                                        className={`flex-1 flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                                          task.status === "done"
                                            ? "bg-green-500/5 border-green-500/20"
                                            : taskOverdue
                                              ? "bg-red-500/5 border-red-500/20"
                                              : "bg-[#0b0811] border-white/5 hover:border-white/15"
                                        }`}
                                        data-testid={`acc-task-${task.id}`}
                                      >
                                        {task.status === "done" ? (
                                          <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-green-400" />
                                        ) : (
                                          <Circle className="w-5 h-5 flex-shrink-0 text-slate-600" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <span className={`text-sm block ${task.status === "done" ? "line-through text-slate-500" : "text-slate-200"}`}>
                                            {task.label}
                                          </span>
                                          {task.deadline && (
                                            <span className={`text-xs mt-0.5 block ${taskOverdue ? "text-red-400" : "text-slate-600"}`}>
                                              Echeance : {formatDate(task.deadline)}
                                            </span>
                                          )}
                                        </div>
                                        {task.responsible && (
                                          <span className="text-xs text-slate-600 flex-shrink-0">{task.responsible}</span>
                                        )}
                                      </button>
                                      <button
                                        onClick={() => triggerFileUpload(acc.accompaniment_id, pi, task.id)}
                                        className="p-2 rounded-lg border border-white/5 bg-[#0b0811] hover:border-[#f7c935]/30 text-slate-500 hover:text-[#f7c935] transition-colors flex-shrink-0"
                                        title="Joindre un fichier"
                                        data-testid={`attach-task-${task.id}`}
                                      >
                                        <Paperclip className="w-4 h-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Phase files */}
                              {phaseFiles.length > 0 && (
                                <div className="mt-3 space-y-1.5" data-testid={`phase-files-${acc.accompaniment_id}-${pi}`}>
                                  <div className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                                    <Paperclip className="w-3 h-3" /> Fichiers joints ({phaseFiles.length})
                                  </div>
                                  {phaseFiles.map(f => (
                                    <div key={f.file_id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0b0811] border border-white/5 text-sm">
                                      <FileText className="w-4 h-4 text-[#3b82f6] flex-shrink-0" />
                                      <span className="text-slate-300 truncate flex-1">{f.original_filename}</span>
                                      <span className="text-xs text-slate-600">{(f.size / 1024).toFixed(0)} Ko</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Phase action buttons */}
                              <div className="mt-3 flex items-center gap-2">
                                <button
                                  onClick={() => triggerFileUpload(acc.accompaniment_id, pi)}
                                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#f7c935] transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5"
                                  data-testid={`attach-phase-${acc.accompaniment_id}-${pi}`}
                                >
                                  {uploadingFile && uploadTarget?.accId === acc.accompaniment_id && uploadTarget?.phaseIndex === pi
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Paperclip className="w-3.5 h-3.5" />}
                                  Joindre un fichier
                                </button>
                                <button
                                  onClick={() => {
                                    setShowNoteForm(showNoteForm === `${acc.accompaniment_id}_${pi}` ? null : `${acc.accompaniment_id}_${pi}`);
                                    setNoteContent("");
                                  }}
                                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#f7c935] transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5"
                                  data-testid={`add-note-phase-${acc.accompaniment_id}-${pi}`}
                                >
                                  <MessageSquare className="w-3.5 h-3.5" /> Ajouter une note
                                </button>
                              </div>

                              {/* Inline note form */}
                              {showNoteForm === `${acc.accompaniment_id}_${pi}` && (
                                <div className="mt-2 bg-[#0b0811] border border-white/10 rounded-xl p-3" data-testid={`note-form-${acc.accompaniment_id}-${pi}`}>
                                  <Textarea
                                    value={noteContent}
                                    onChange={e => setNoteContent(e.target.value)}
                                    placeholder="Votre note ou commentaire..."
                                    rows={3}
                                    className="bg-[#161020] border-white/10 text-white placeholder:text-slate-500 text-sm mb-2"
                                    data-testid={`note-input-${acc.accompaniment_id}-${pi}`}
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => { setShowNoteForm(null); setNoteContent(""); }}
                                      className="text-slate-400 text-xs"
                                    >
                                      <X className="w-3 h-3 mr-1" /> Annuler
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => submitNote(acc.accompaniment_id)}
                                      disabled={submittingNote || !noteContent.trim()}
                                      className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] text-xs"
                                      data-testid={`submit-note-${acc.accompaniment_id}-${pi}`}
                                    >
                                      {submittingNote ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                                      Envoyer
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Notes section for this accompaniment */}
                      {(notes[acc.accompaniment_id] || []).length > 0 && (
                        <div className="mt-6 ml-4" data-testid={`acc-notes-${acc.accompaniment_id}`}>
                          <h4 className="text-sm font-bold text-slate-400 mb-3 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" /> Notes ({notes[acc.accompaniment_id].length})
                          </h4>
                          <div className="space-y-2">
                            {(notes[acc.accompaniment_id] || []).map(n => (
                              <div key={n.note_id} className="bg-[#0b0811] border border-white/5 rounded-lg px-4 py-3" data-testid={`acc-note-${n.note_id}`}>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-xs font-medium" style={{ color: "#f7c935" }}>{n.author_name || "Inconnu"}</span>
                                  <span className="text-[10px] text-slate-600">{new Date(n.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                                </div>
                                <p className="text-sm text-slate-300">{n.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileUpload}
        data-testid="acc-file-input"
      />
      {showAgent && (
        <div className="fixed bottom-6 right-6 w-[420px] z-50 shadow-2xl shadow-black/50" data-testid="floating-chat-widget">
          <AgentChat agentType="conseiller" agentName="Expert Strategie" agentColor="#f7c935"
            context={{}} onClose={() => setShowAgent(false)} />
        </div>
      )}
    </div>
  );
}
